import {isFilled} from '@prismicio/client'
import {PrismicNextImage} from '@prismicio/next/pages'
import {PrismicRichText} from '@prismicio/react'
import cn from 'classnames'
import {useLinkResolver} from 'prismicio'
import type {CtaSmallSlice} from 'prismicio-types'
import {memo} from 'react'
import {ActionButton} from 'src/common/ActionButton'
import {ButtonV2} from 'src/common/ButtonV2'
import type {Context} from 'src/common/types'

const CtaSmall = (props: {slice: CtaSmallSlice; context: Context}) => {
  const {linkResolver} = useLinkResolver()
  return (
    <section
      className={cn('grid scroll-mt-16 grid-cols-main items-center gap-y-8 sm:gap-y-5', {
        'bg-5a7269': props.slice.primary.color === 'Default' || !props.slice.primary.color,
        'bg-3f8896': props.slice.primary.color === 'Blue',
        'bg-926077': props.slice.primary.color === 'Purple',
        'bg-b56659': props.slice.primary.color === 'Red',
        'bg-b47b52': props.slice.primary.color === 'Orange',
      })}
      id={String(props.slice.primary.id_anchor)}
    >
      <div className="col-[inner] row-start-1 grid gap-y-6 pt-10 md:col-end-[track-end_7] md:py-10 lg:col-end-[track-end_7]">
        <PrismicRichText
          components={{
            heading2: ({children}) => (
              <h2 className="text-36 leading-11 -tracking-common text-ffffff sm:text-32 sm:leading-10 md:text-36 md:leading-11 xl:text-48 xl:leading-14 [&_>_br]:max-md:content-[''] [&_>_br]:max-md:after:content-['_']">
                {children}
              </h2>
            ),
            heading3: ({children}) => (
              <h2 className="text-32 leading-10 -tracking-common text-ffffff lg:text-36 lg:leading-11 [&_>_br]:max-md:content-[''] [&_>_br]:max-md:after:content-['_']">
                {children}
              </h2>
            ),
            strong: ({children}) => <strong className="tracking-normal">{children}</strong>,
          }}
          field={props.slice.primary.title}
        />

        <div className="grid grid-flow-col gap-x-2 justify-self-start">
          {props.slice.primary.primary_link_anchor && (
            <ButtonV2
              href={`#${props.slice.primary.primary_link_anchor}`}
              trackingData={{
                mktgId: 'cta-button',
                location: 'cta-small-section',
                dataType: 'primary-button',
                pageName: props.context.pageUid || '',
              }}
              scroll={false}
              variant="primary"
            >
              {props.slice.primary.link_label}
            </ButtonV2>
          )}

          {!props.slice.primary.primary_link_anchor && (
            <ActionButton kind="demo" data-page-location="bottom">
              {props.slice.primary.link_label}
            </ActionButton>
          )}

          {(isFilled.link(props.slice.primary.secondary_link) || props.slice.primary.secondary_link_anchor) && (
            <ButtonV2
              href={
                props.slice.primary.secondary_link_anchor
                  ? `#${props.slice.primary.secondary_link_anchor}`
                  : linkResolver(props.slice.primary.secondary_link, 'secondary-button-cta-small')
              }
              {...(props.slice.primary.secondary_link_anchor && {scroll: false})}
              {...(isFilled.link(props.slice.primary.secondary_link) &&
                props.slice.primary.secondary_link.link_type === 'Web' && {
                  target: props.slice.primary.secondary_link.target,
                })}
              trackingData={{
                mktgId: 'secondary-cta-button',
                location: 'cta-small-section',
                dataType: 'secondary-button',
                pageName: props.context.pageUid || '',
              }}
              variant="secondary"
            >
              {props.slice.primary.secondary_link_label}
            </ButtonV2>
          )}
        </div>
      </div>

      {isFilled.image(props.slice.primary.image_mobile) && (
        <PrismicNextImage
          className="col-[outer] w-full max-w-none md:hidden"
          field={props.slice.primary.image_mobile}
        />
      )}

      <PrismicNextImage
        className={cn(
          'col-start-[track-start_6] col-end-[entire-end] row-start-1 size-full lg:col-start-[track-start_7] xl:col-start-[track-start_8] 2xl:col-start-[track-start_9]',
          {
            'max-md:hidden': isFilled.image(props.slice.primary.image_mobile),
          },
        )}
        field={props.slice.primary.image}
      />
    </section>
  )
}

export default memo(CtaSmall)
